import "react-pdf/dist/esm/Page/TextLayer.css";

import Close from "@mui/icons-material/Close";
import { TabContext, TabPanel } from "@mui/lab";
import { Tab } from "@mui/material";
import NoImportBoard from "assets/img/svg/emptyState/noImportBoard";
import classNames from "classnames";
import ButtonBase from "components/shared/ButtonBase";
import { Spinner } from "components/shared/Spinner";
import StatusChip from "components/shared/StatusChip";
import StyledButton from "components/shared/StyledButton";
import { StyledTabs } from "components/shared/StyledTabs";
import { transferWorkLabel } from "containers/TransferTermFormContainer/utils";
import { salesStatusRules } from "containers/TransferTerms";
import { formatLocaleDateString } from "helpers/utils";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page } from "react-pdf";
import type { ITransferTermSale } from "store/transferTermSalesStore";

import styles from "./style.module.scss";

interface IDocumentsDrawer {
    /**
     * Data of payment selected.
     */
    termSale: ITransferTermSale | undefined;
    /**
     * Triggers opening modals for adding statement or invoice.
     */
    handleOpeningModal: (
        currentSale: ITransferTermSale | undefined,
        modalType: string,
        mode?: string,
        event?: any,
    ) => Promise<void>;
    handleClose: () => void;
    /**
     * Whether company has Bexio connected.
     */
    hasBexio: boolean;
}

/**
 * Drawer opened from TransferTermSalesTable when clicking on line. Displays PDf statement and invoice.
 */
const DocumentsDrawer = ({ termSale, handleOpeningModal, handleClose, hasBexio }: IDocumentsDrawer) => {
    const [t] = useTranslation();
    const [tabValue, setTabValue] = useState("0");
    const [numberPages, setNumberPages] = useState<number | null>(null);
    const pdfFrame = useRef<HTMLIFrameElement | null>(null);
    const [pdfLoaded, setPdfLoaded] = useState(false);

    const hasInvoice = termSale?.invoiceUrl;
    const isBexioInvoice = hasInvoice && termSale?.invoiceUrl.includes("office.bexio");
    const hasStatement = termSale?.statementUrl;

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setTabValue(newValue);
        setNumberPages(null);
        setPdfLoaded(false);
    };

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumberPages(numPages);
    };

    const renderDocumentLoader = () => {
        return (
            <div className={styles["document-loader"]}>
                <Spinner />
            </div>
        );
    };

    // const reloadFrame = (frame: any) => {
    //     frame.src = frame.src; // eslint-disable-line
    // };

    // Resets iframe src if for some reason it's not loading.
    // useEffect(() => {
    //     let timerId;
    //     if (!pdfLoaded) {
    //         timerId = setInterval(() => reloadFrame(pdfFrame?.current), 2000);
    //         return () => clearInterval(timerId);
    //     }
    // }, [pdfLoaded]);

    return (
        <div className={styles["documents-drawer-container"]}>
            <TabContext value={tabValue}>
                <div className={styles["drawer-header"]}>
                    <div className={styles["header-left"]}>
                        <h1>
                            {t("TransferTerms.DocumentsDrawer.Title", { titleName: transferWorkLabel(termSale ?? {}) })}
                        </h1>
                        <span className={styles["transferee-name"]}>
                            {t("TransferTerms.Drawer.TermTransferee", { name: termSale?.transfereeName })}
                        </span>
                        <span className={styles["transferee-name"]}>
                            {t("TransferTerms.DocumentsDrawer.Expected", {
                                dueDate: termSale?.dueDate ? formatLocaleDateString({ date: termSale?.dueDate }) : "-",
                            })}
                        </span>
                        <span className={styles["transferee-name"]}>
                            {t("TransferTerms.DocumentsDrawer.Status")}{" "}
                            <StatusChip status={termSale?.status} statusRules={salesStatusRules()} />
                        </span>
                    </div>
                    <div className="header-right">
                        <ButtonBase
                            variant="text"
                            styles={{ border: "none", width: "unset", height: "1.9rem" }}
                            onClick={handleClose}
                        >
                            <Close sx={{ color: "black" }} className="term-details-close" />
                        </ButtonBase>
                    </div>
                </div>
                <div className={styles["drawer-tabs-row"]}>
                    <StyledTabs onChange={handleTabChange} className={styles["drawer-tabs"]} value={tabValue}>
                        <Tab value="0" label={t("TransferTerms.DocumentsDrawer.Statement")} />
                        <Tab value="1" label={t("TransferTerms.DocumentsDrawer.Invoice")} />
                    </StyledTabs>
                    {((tabValue === "0" && hasStatement) || (tabValue === "1" && hasInvoice)) && (
                        <StyledButton
                            buttonType="primary"
                            onClick={(e) =>
                                tabValue === "0"
                                    ? handleOpeningModal(termSale, "addRights", "edit", e)
                                    : handleOpeningModal(termSale, "addInvoice", "edit", e)
                            }
                        >
                            {tabValue === "0"
                                ? t("TransferTerms.DocumentsDrawer.UpdateStatement")
                                : t("TransferTerms.DocumentsDrawer.UpdateInvoice")}
                        </StyledButton>
                    )}
                </div>

                {/* STATEMENT */}
                <TabPanel
                    value="0"
                    className={classNames({
                        [styles["tab-panel"]]: !!numberPages,
                    })}
                >
                    {hasStatement && tabValue === "0" ? (
                        <Document
                            file={termSale?.statementUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={renderDocumentLoader()}
                        >
                            {Array.apply(null, Array(numberPages))
                                .map((x, i) => i + 1)
                                .map((page) => (
                                    <Page
                                        renderAnnotationLayer={false}
                                        pageNumber={page}
                                        scale={1.5}
                                        key={`page${page}o${numberPages}`}
                                    />
                                ))}
                        </Document>
                    ) : (
                        // This was used to display with Google Docs viewer:
                        // <div style={{ height: "100vh" }}>
                        //     {!pdfLoaded && !pdfFrame?.current && (
                        //         <div className={styles["document-loader"]}>
                        //             <Spinner />
                        //         </div>
                        //     )}
                        //     {
                        //         <iframe
                        //             src={`https://docs.google.com/viewerng/viewer?url=${termSale?.statementUrl}&embedded=true`}
                        //             className={styles["document-frame"]}
                        //             ref={pdfFrame}
                        //             onLoad={() => setPdfLoaded(true)}
                        //         />
                        //     }
                        // </div>
                        <div className={styles["empty-document"]}>
                            <div className={styles["action"]}>
                                <span>{t("TransferTerms.DocumentsDrawer.NoStatement")}</span>
                                <StyledButton
                                    buttonType="primary"
                                    onClick={(e) => handleOpeningModal(termSale, "addRights", "edit", e)}
                                >
                                    {t("TransferTerms.DocumentsDrawer.AddStatement")}
                                </StyledButton>
                            </div>
                            <NoImportBoard />
                        </div>
                    )}
                </TabPanel>

                {/* INVOICE */}
                <TabPanel
                    value="1"
                    className={classNames({
                        [styles["tab-panel"]]: !!numberPages,
                    })}
                >
                    {hasInvoice && !isBexioInvoice ? (
                        <Document
                            file={termSale?.invoiceUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={renderDocumentLoader()}
                        >
                            {Array.apply(null, Array(numberPages))
                                .map((x, i) => i + 1)
                                .map((page) => (
                                    <Page
                                        renderAnnotationLayer={false}
                                        pageNumber={page}
                                        scale={1.5}
                                        key={`page${page}o${numberPages}`}
                                    />
                                ))}
                        </Document>
                    ) : hasInvoice && isBexioInvoice ? (
                        <div className={styles["empty-document"]}>
                            <div className={styles["action"]}>
                                <span>{t("TransferTerms.DocumentsDrawer.BexioInvoice")}</span>
                                <StyledButton
                                    buttonType="primary"
                                    onClick={() => window.open(termSale?.invoiceUrl, "_blank")}
                                >
                                    {t("TransferTerms.DocumentsDrawer.SeeBexioInvoice")}
                                </StyledButton>
                            </div>
                            <NoImportBoard />
                        </div>
                    ) : (
                        <div className={styles["empty-document"]}>
                            <div className={styles["action"]}>
                                <span>{t("TransferTerms.DocumentsDrawer.NoInvoice")}</span>
                                <StyledButton
                                    buttonType="primary"
                                    onClick={(e) => handleOpeningModal(termSale, "addInvoice", "edit", e)}
                                >
                                    {hasBexio
                                        ? t("TransferTerms.DocumentsDrawer.MakeInvoice")
                                        : t("TransferTerms.DocumentsDrawer.AddInvoice")}
                                </StyledButton>
                            </div>
                            <NoImportBoard />
                        </div>
                    )}
                </TabPanel>
            </TabContext>
        </div>
    );
};

export default DocumentsDrawer;
